/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `threeDSPopup` for the Caintra Connect frontend.
 * @module utils/payment/threeDSPopup
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
const THREE_DS_TIMEOUT_MS = 600000;
const POPUP_POLL_INTERVAL_MS = 500;

/**
 * Builds the loading HTML shown in the 3DS popup while authentication prepares.
 * @param {{ preparingText: string, processingText: string }} labels
 * @returns {string}
 */
export const buildThreeDSLoadingHtml = ({ preparingText, processingText }) => `
  <!DOCTYPE html>
  <html>
    <head>
      <title>${processingText}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
          margin: 0;
          background-color: #f5f5f5;
        }
        .container {
          text-align: center;
        }
        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #3498db;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="spinner"></div>
        <p>${preparingText}</p>
      </div>
    </body>
  </html>
`;

/**
 * Opens a blank popup window for 3DS authentication.
 * @param {{ preparingText: string, processingText: string }} labels
 * @returns {Window|null}
 */
export const openThreeDSPopup = ({ preparingText, processingText }) => {
  const popup = window.open(
    "about:blank",
    "openpay3ds",
    "width=500,height=600,scrollbars=yes,resizable=yes",
  );

  if (!popup) {
    return null;
  }

  popup.document.write(
    buildThreeDSLoadingHtml({ preparingText, processingText }),
  );

  return popup;
};

/**
 * Navigates a popup to the 3DS URL and waits for completion, closure, or timeout.
 * @param {Object} params
 * @param {Window} params.popup
 * @param {string} params.authenticationUrl
 * @param {string} params.origin - Expected postMessage origin
 * @param {() => void} [params.onAuthenticating]
 * @param {(popupWasClosed: boolean) => void} params.onComplete
 * @param {string} params.timeoutMessage
 * @returns {Promise<void>}
 */
export const waitForThreeDSCompletion = ({
  popup,
  authenticationUrl,
  origin,
  onAuthenticating,
  onComplete,
  timeoutMessage,
}) => {
  return new Promise((resolve, reject) => {
    if (!popup || popup.closed) {
      reject(new Error("Popup window is not available"));
      return;
    }

    popup.location.href = authenticationUrl;
    onAuthenticating?.();

    const cleanup = (pollInterval, timeoutId, messageHandler) => {
      clearInterval(pollInterval);
      clearTimeout(timeoutId);
      window.removeEventListener("message", messageHandler);
    };

    const messageHandler = (event) => {
      if (event.origin !== origin) {
        return;
      }

      if (event.data.type === "3DS_COMPLETE") {
        cleanup(pollInterval, timeoutId, messageHandler);
        onComplete(false);
        resolve();
      }
    };
    window.addEventListener("message", messageHandler);

    const pollInterval = setInterval(() => {
      if (popup.closed) {
        cleanup(pollInterval, timeoutId, messageHandler);
        onComplete(true);
        resolve();
      }
    }, POPUP_POLL_INTERVAL_MS);

    const timeoutId = setTimeout(() => {
      if (!popup.closed) {
        cleanup(pollInterval, timeoutId, messageHandler);
        popup.close();
        reject(new Error(timeoutMessage));
      }
    }, THREE_DS_TIMEOUT_MS);
  });
};

/**
 * Closes a popup window if it is still open.
 * @param {Window|null|undefined} popup
 */
export const closePopupIfOpen = (popup) => {
  if (popup && !popup.closed) {
    popup.close();
  }
};
